/**
 * Programmatic city data for local SEO landing pages.
 * Each city has authentic, unique copy per market — never templated text.
 */

export const CITIES = {
  berlin: {
    slug: "berlin",
    name: "Berlin",
    nameLocative: { en: "in Berlin", de: "in Berlin" },
    region: "Berlin",
    population: "3.8M",
    hook: {
      en: "Berlin's startup density means design expectations are agency-tier — even from your first hire. NetoVibe delivers custom website development that looks native to Mitte, Kreuzberg, or Prenzlauer Berg — without a Mitte agency invoice.",
      de: "Berlins Startup-Dichte hebt die Design-Erwartungen auf Agentur-Niveau — schon beim ersten Angebot. NetoVibe liefert individuelle Website-Entwicklung, die in Mitte, Kreuzberg oder Prenzlauer Berg wie eine große Agentur wirkt — ohne Mitte-Rechnung.",
    },
    signals: {
      en: [
        "Bilingual EN/DE for international B2B customers",
        "Fast enough for the Berlin Startup Nights demo",
        "GDPR & TTDSG-compliant by default",
      ],
      de: [
        "Zweisprachig EN/DE für internationale B2B-Kunden",
        "Schnell genug für die Berlin Startup Nights Demo",
        "DSGVO & TTDSG-konform ab Werk",
      ],
    },
  },
  munich: {
    slug: "munich",
    name: "Munich",
    nameLocative: { en: "in Munich", de: "in München" },
    region: "Bayern",
    population: "1.5M",
    hook: {
      en: "Munich businesses demand craftsmanship. NetoVibe builds professional business websites for München, Bogenhausen, Schwabing and the greater Bayern region — with a design language that reads Bavaria-serious, not Silicon-Valley-loud.",
      de: "Münchner Unternehmen erwarten Handwerk. NetoVibe erstellt professionelle Firmenwebsites für München, Bogenhausen, Schwabing und ganz Bayern — mit einer Bildsprache, die bayerisch-solide wirkt, nicht Silicon-Valley-laut.",
    },
    signals: {
      en: [
        "Portrait-oriented content for Bavarian trust signals",
        "Impressum & Datenschutz drafted by default",
        "Ready for Handwerkskammer & IHK style briefs",
      ],
      de: [
        "Vertrauenssignale im bayerischen Stil",
        "Impressum & Datenschutz standardmäßig integriert",
        "Passend für Handwerkskammer- und IHK-Kontext",
      ],
    },
  },
  penzberg: {
    slug: "penzberg",
    name: "Penzberg",
    nameLocative: { en: "in Penzberg", de: "in Penzberg" },
    region: "Oberbayern",
    population: "16k",
    hook: {
      en: "Penzberg is our home market. We built Zemka Gebäudereinigung's website here — a local cleaning company that now looks as professional online as they are on site. Small town, big-city polish.",
      de: "Penzberg ist unser Heimatmarkt. Hier haben wir die Website von Zemka Gebäudereinigung gebaut — ein lokales Reinigungsunternehmen, das online jetzt so professionell wirkt wie vor Ort. Kleine Stadt, Großstadt-Politur.",
    },
    signals: {
      en: [
        "Zemka Gebäudereinigung — a proven Penzberg case study",
        "In-person handover possible for local clients",
        "Priced for small-town budgets, engineered like a city agency",
      ],
      de: [
        "Zemka Gebäudereinigung — echte Penzberger Case Study",
        "Persönliche Übergabe für lokale Kunden möglich",
        "Preise für kleinstädtische Budgets, Qualität wie eine Großstadt-Agentur",
      ],
    },
  },
  hamburg: {
    slug: "hamburg",
    name: "Hamburg",
    nameLocative: { en: "in Hamburg", de: "in Hamburg" },
    region: "Hamburg",
    population: "1.9M",
    hook: {
      en: "From Speicherstadt logistics to St. Pauli service businesses, Hamburg companies need websites that ship as fast as the container port. NetoVibe delivers custom development with a maritime-clean design sensibility.",
      de: "Ob Speicherstadt-Logistik oder St.-Pauli-Dienstleistung — Hamburger Unternehmen brauchen Websites, die so schnell laufen wie der Containerhafen liefert. NetoVibe bietet individuelle Entwicklung mit hanseatisch-klarem Design.",
    },
    signals: {
      en: [
        "Fast: sub-second first paint on 4G",
        "Bilingual for cross-Nordsee B2B customers",
        "Analytics-ready for shipping and services verticals",
      ],
      de: [
        "Schnell: First Paint unter 1 s auf 4G",
        "Zweisprachig für internationale Nordsee-Kunden",
        "Analytics-ready für Logistik & Dienstleistungen",
      ],
    },
  },
  frankfurt: {
    slug: "frankfurt",
    name: "Frankfurt",
    nameLocative: { en: "in Frankfurt", de: "in Frankfurt" },
    region: "Hessen",
    population: "790k",
    hook: {
      en: "Frankfurt runs on trust signals — banking, law, consulting. NetoVibe engineers professional business websites that make Rhein-Main SMBs and boutique advisors look every bit as institutional as their Zeil-address competitors.",
      de: "Frankfurt lebt von Vertrauenssignalen — Banking, Recht, Beratung. NetoVibe baut professionelle Firmen-Websites, die Rhein-Main-Mittelstand und Boutique-Berater so institutionell wirken lassen wie ihre Zeil-Adress-Konkurrenz.",
    },
    signals: {
      en: [
        "Corporate-tier typography, boutique attention to detail",
        "Compliance-friendly (Impressum + Datenschutz done)",
        "English + German for international finance clients",
      ],
      de: [
        "Corporate-Typografie, Boutique-Sorgfalt",
        "Compliance-freundlich (Impressum + Datenschutz enthalten)",
        "Englisch + Deutsch für internationale Finanzkunden",
      ],
    },
  },
  cologne: {
    slug: "cologne",
    name: "Cologne",
    nameLocative: { en: "in Cologne", de: "in Köln" },
    region: "Nordrhein-Westfalen",
    population: "1.1M",
    hook: {
      en: "Köln's local business scene is friendly, dense, and competitive. NetoVibe builds affordable custom websites that stand out in the Belgisches Viertel and Rhein-Auen — without a Mittelstands-Cliché in sight.",
      de: "Kölns lokaler Business-Kosmos ist herzlich, dicht und wettbewerbsintensiv. NetoVibe erstellt bezahlbare, individuelle Websites, die im Belgischen Viertel oder Rhein-Auen auffallen — ohne Mittelstands-Klischee.",
    },
    signals: {
      en: [
        "Warmer palettes on request — Köln-friendly",
        "Karneval-safe uptime :)",
        "Localised SEO for NRW searches",
      ],
      de: [
        "Wärmere Paletten auf Wunsch — Köln-kompatibel",
        "Karneval-sichere Uptime :)",
        "Lokales SEO für NRW-Suchen",
      ],
    },
  },
  stuttgart: {
    slug: "stuttgart",
    name: "Stuttgart",
    nameLocative: { en: "in Stuttgart", de: "in Stuttgart" },
    region: "Baden-Württemberg",
    population: "634k",
    hook: {
      en: "Stuttgart is engineering country — Porsche, Bosch, Mercedes-Benz set the local bar for precision. NetoVibe delivers websites with the same tolerance for imprecision as a Swabian engineer: zero.",
      de: "Stuttgart ist Ingenieursland — Porsche, Bosch, Mercedes-Benz setzen den lokalen Maßstab für Präzision. NetoVibe liefert Websites mit derselben Toleranz für Ungenauigkeit wie ein schwäbischer Ingenieur: null.",
    },
    signals: {
      en: [
        "Precision-tuned for engineering & Mittelstand clients",
        "Fast delivery aligned with Baden-Württemberg efficiency",
        "Bilingual output ready for global automotive suppliers",
      ],
      de: [
        "Präzise abgestimmt auf Ingenieurs- & Mittelstands-Kunden",
        "Schnelle Umsetzung im Baden-Württemberg-Takt",
        "Zweisprachig für globale Automotive-Zulieferer",
      ],
    },
  },
  dusseldorf: {
    slug: "dusseldorf",
    name: "Düsseldorf",
    nameLocative: { en: "in Düsseldorf", de: "in Düsseldorf" },
    region: "Nordrhein-Westfalen",
    population: "620k",
    hook: {
      en: "Düsseldorf mixes NRW industry with fashion-forward retail on Kö. NetoVibe builds websites that look boardroom-serious on the Rheinturm side and boutique-cool on the Altstadt side of your business.",
      de: "Düsseldorf verbindet NRW-Industrie mit fashion-affinem Einzelhandel auf der Kö. NetoVibe baut Websites, die auf der Rheinturm-Seite Ihres Geschäfts vorstandsseriös und auf der Altstadt-Seite boutique-lässig wirken.",
    },
    signals: {
      en: [
        "Retail-ready templates for Kö-tier boutiques",
        "B2B-precise typography for NRW industry",
        "Ready for tri-lingual expansion (EN/DE/JA)",
      ],
      de: [
        "Retail-Templates auf Kö-Niveau",
        "B2B-Typografie für NRW-Industrie",
        "Bereit für dreisprachige Erweiterung (EN/DE/JA)",
      ],
    },
  },
  nuremberg: {
    slug: "nuremberg",
    name: "Nuremberg",
    nameLocative: { en: "in Nuremberg", de: "in Nürnberg" },
    region: "Bayern",
    population: "525k",
    hook: {
      en: "Nürnberg's Handwerker culture is thriving — plumbers, electricians, cleaners, bakeries. NetoVibe builds professional business websites at prices that make Franconian sense — pragmatic, honest, no fluff.",
      de: "Nürnbergs Handwerker-Kultur boomt — Installateure, Elektriker, Reinigungsfirmen, Bäckereien. NetoVibe erstellt professionelle Firmenwebsites zu Preisen, die fränkisch Sinn ergeben — pragmatisch, ehrlich, ohne Bling.",
    },
    signals: {
      en: [
        "Made for Handwerker & Mittelstand",
        "Booking-ready contact forms out of the box",
        "Franconian-honest pricing, agency-grade delivery",
      ],
      de: [
        "Gebaut für Handwerker & Mittelstand",
        "Buchungsformulare ab Werk enthalten",
        "Fränkisch-ehrliche Preise, Agentur-Qualität",
      ],
    },
  },
  leipzig: {
    slug: "leipzig",
    name: "Leipzig",
    nameLocative: { en: "in Leipzig", de: "in Leipzig" },
    region: "Sachsen",
    population: "630k",
    hook: {
      en: "Leipzig has quietly become one of Germany's most exciting business cities — creative agencies, book publishers, service startups. NetoVibe delivers custom development that fits Plagwitz creatives and Zentrum executives alike.",
      de: "Leipzig ist leise zu einer der spannendsten deutschen Business-Städte geworden — Kreativagenturen, Verlage, Service-Startups. NetoVibe liefert individuelle Websites, die zu Plagwitz-Kreativen und Zentrum-Executives gleichermaßen passen.",
    },
    signals: {
      en: [
        "Modular design system for creative-industry clients",
        "Speed-optimised for East German infrastructure",
        "Bilingual for university & research clients",
      ],
      de: [
        "Modulares Design-System für die Kreativbranche",
        "Speed-optimiert für ostdeutsche Infrastruktur",
        "Zweisprachig für Universitäts- & Forschungskunden",
      ],
    },
  },
  vienna: {
    slug: "vienna",
    name: "Vienna",
    nameLocative: { en: "in Vienna", de: "in Wien" },
    region: "Österreich",
    population: "1.9M",
    hook: {
      en: "Vienna clients expect elegance without ostentation — 1. Bezirk taste on a Landstraße budget. NetoVibe delivers custom websites that fit both.",
      de: "Wiener Kunden erwarten Eleganz ohne Effekthascherei — Ersten-Bezirks-Geschmack zu Landstraße-Budget. NetoVibe liefert individuelle Websites, die beides erfüllen.",
    },
    signals: {
      en: [
        "AT-compliant Impressum + DSGVO defaults",
        "German + English by default",
        "Priced in EUR, same day AT invoicing",
      ],
      de: [
        "AT-konformes Impressum + DSGVO ab Werk",
        "Deutsch + Englisch standardmäßig",
        "Abrechnung in EUR, taggleiche AT-Rechnung",
      ],
    },
  },
  zurich: {
    slug: "zurich",
    name: "Zurich",
    nameLocative: { en: "in Zurich", de: "in Zürich" },
    region: "Schweiz",
    population: "440k",
    hook: {
      en: "Zurich businesses expect Swiss-precise typography, discrete conversion design and multi-language delivery from day one. NetoVibe ships Zurich-tier quality at a price that surprises even in Switzerland.",
      de: "Zürcher Unternehmen erwarten Schweizer Präzisions-Typografie, dezente Conversion-Optimierung und mehrsprachige Auslieferung ab Tag eins. NetoVibe liefert Zürich-Qualität zu einem Preis, der auch in der Schweiz überrascht.",
    },
    signals: {
      en: [
        "CH-ready trilingual delivery (EN/DE/FR)",
        "Swiss-precise typographic system",
        "Priced sanely for CH SMB reality",
      ],
      de: [
        "CH-fähige dreisprachige Auslieferung (EN/DE/FR)",
        "Schweizer präzises Typografie-System",
        "Vernünftige Preise für CH-KMU-Realität",
      ],
    },
  },
};

export const CITY_SLUGS = Object.keys(CITIES);
